import React from 'react';
import { Link } from 'react-router-dom';

const Terms: React.FC = () => {
  return (
    <div className="min-h-screen bg-[#F3F3F3] text-[#132D28]">
      <header className="fixed top-0 w-full bg-[#F3F3F3]/90 backdrop-blur-sm border-b border-[#204941]/10 z-40">
        <nav className="container mx-auto py-4">
          <Link to="/" className="text-lg font-semibold text-[#204941]">Watchi.app</Link>
        </nav>
      </header>

      <main className="container mx-auto pt-28 pb-16">
        <article className="bg-white rounded-3xl shadow-sm px-6 md:px-12 py-10">
          <header className="mb-10">
            <h1 className="text-3xl md:text-4xl font-semibold text-[#132D28]">Terms of Use for Watchi.app</h1>
            <p className="mt-4 text-sm text-[#204941]/60">Effective as of 03 October 2025</p>
          </header>

          <section className="space-y-8 text-[#204941]/80">
            <div className="p-5 bg-[#F3F3F3] rounded-2xl border border-[#204941]/10">
              <h2 className="text-lg font-semibold text-[#204941] mb-2">Friendly reminder</h2>
              <p className="text-sm text-[#204941]/70">
                Watchi.app helps you customize your Apple Watch. It does not affect Apple Watch system settings and it does not provide medical or diagnostic services.
              </p>
            </div>

            <div>
              <h2 className="text-xl font-semibold text-[#204941] mb-3">1. Agreement to terms</h2>
              <p>
                By installing, accessing, or using Watchi.app you agree to follow these terms and our Privacy Promise. If you disagree with any part, please remove Watchi.app from your device.
              </p>
            </div>

            <div>
              <h2 className="text-xl font-semibold text-[#204941] mb-3">2. What Watchi.app provides</h2>
              <p>
                We curate digital watch faces, wallpapers, widget packs, and companion tips for Apple Watch users. Some items are free; premium collections require an active subscription or one-time purchase.
              </p>
            </div>

            <div>
              <h2 className="text-xl font-semibold text-[#204941] mb-3">3. Account eligibility</h2>
              <p>
                You must be at least 13 years old to use Watchi.app. Users under 18 must have permission from a parent or guardian. Ensure your Apple ID details are accurate and kept secure.
              </p>
            </div>

            <div>
              <h2 className="text-xl font-semibold text-[#204941] mb-3">4. Purchases and billing</h2>
              <ul className="list-disc pl-5 space-y-2">
                <li>Payments are processed by Apple. We do not store your card details.</li>
                <li>Subscriptions renew automatically unless cancelled at least 24 hours before the period ends.</li>
                <li>You can manage or cancel a plan in iOS Settings → Apple ID → Subscriptions.</li>
                <li>Trial periods convert to paid plans if not cancelled before they expire.</li>
              </ul>
            </div>

            <div>
              <h2 className="text-xl font-semibold text-[#204941] mb-3">5. License granted</h2>
              <p>
                We provide you with a personal, limited, revocable license to use Watchi.app for non-commercial purposes. You may not redistribute content packages, resell our watch faces, or reverse engineer the app.
              </p>
            </div>

            <div>
              <h2 className="text-xl font-semibold text-[#204941] mb-3">6. Creator uploads</h2>
              <p>
                Designers who submit watch faces warrant they own the rights to the content. By uploading, you grant Watchi.app a licence to host, showcase, and deliver the creative assets within the app and on our marketing channels.
              </p>
            </div>

            <div>
              <h2 className="text-xl font-semibold text-[#204941] mb-3">7. Acceptable use</h2>
              <ul className="list-disc pl-5 space-y-2">
                <li>Do not attempt to copy or scrape the service at scale.</li>
                <li>Avoid sharing offensive or infringing content.</li>
                <li>Respect other users and creators in community areas.</li>
                <li>Report bugs or vulnerabilities responsibly to <a href="mailto:support@watchi.app" className="text-[#204941] underline hover:text-[#68C1AE]">support@watchi.app</a>.</li>
              </ul>
            </div>

            <div>
              <h2 className="text-xl font-semibold text-[#204941] mb-3">8. Intellectual property</h2>
              <p>
                Watchi.app, our name, logos, and the curated collections remain our property or that of respective creators. Apple, Apple Watch, and related marks belong to Apple Inc. and are used under fair use references.
              </p>
            </div>

            <div>
              <h2 className="text-xl font-semibold text-[#204941] mb-3">9. Warranty disclaimer</h2>
              <p>
                Watchi.app is provided “as is”. We aim for a smooth experience but cannot guarantee uninterrupted availability or error-free operation. Customizations are cosmetic; Apple Watch system performance is unchanged.
              </p>
            </div>

            <div>
              <h2 className="text-xl font-semibold text-[#204941] mb-3">10. Limitation of liability</h2>
              <p>
                To the extent allowed by law, Watchi.app and its team are not liable for indirect or consequential damages arising from use of the service. Our total liability is limited to the amount you paid during the previous 3 months.
              </p>
            </div>

            <div>
              <h2 className="text-xl font-semibold text-[#204941] mb-3">11. Ending the relationship</h2>
              <p>
                You may stop using Watchi.app at any time. We may suspend or terminate access if we detect misuse, unpaid charges, or legal obligations. Upon termination, licenses granted to you end immediately.
              </p>
            </div>

            <div>
              <h2 className="text-xl font-semibold text-[#204941] mb-3">12. Governing law</h2>
              <p>
                These terms are governed by the laws of Estonia, with disputes resolved in Tallinn courts, unless local consumer law states otherwise.
              </p>
            </div>

            <div>
              <h2 className="text-xl font-semibold text-[#204941] mb-3">13. Updates to the terms</h2>
              <p>
                We may modify these terms as Watchi.app evolves. Changes take effect when posted here. If the updates are material, we will notify you through the app or by email before they become active.
              </p>
            </div>

            <div>
              <h2 className="text-xl font-semibold text-[#204941] mb-3">14. Contact</h2>
              <p>
                Questions? Reach out to <a href="mailto:support@watchi.app" className="text-[#204941] underline hover:text-[#68C1AE]">support@watchi.app</a> and we will be happy to assist.
              </p>
            </div>
          </section>
        </article>
      </main>
    </div>
  );
};

export default Terms;